import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import CreditsBadge from "./CreditsBadge.jsx";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { useAuth } from "../lib/useAuth";

const styles = {
  bar: {
    position: "sticky",
    top: 0,
    zIndex: 40,
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
  },
  wrap: {
    maxWidth: 1120,
    width: "100%",
    margin: "0 auto",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    boxSizing: "border-box",
  },
  left: { display: "flex", alignItems: "center", gap: 16 },
  brand: { 
    fontSize: 20, 
    fontWeight: 700, 
    color: "#4f46e5", 
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  logo: {
    width: 32,
    height: 32,
    objectFit: "contain"
  },
  nav: { display: "flex", alignItems: "center", gap: 12 },
  link: { padding: "8px 10px", borderRadius: 8, color: "#374151", textDecoration: "none" },
  linkActive: { color: "#4338ca", background: "#eef2ff", fontWeight: 600 },
  right: { display: "flex", alignItems: "center", gap: 12 },
  button: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "6px 12px",
    background: "white",
    cursor: "pointer",
  },
  hi: { fontSize: 14, color: "#6b7280" },
};

export default function Navbar() {
  const { user, signOut } = useAuth?.() ?? {};
  const [open, setOpen] = useState(false);
  const itemClass = ({ isActive }) => ({
    ...styles.link,
    ...(isActive ? styles.linkActive : null),
  });

  // Prevent page scroll when drawer is open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header style={styles.bar}>
      <div style={styles.wrap}>
        {/* LEFT: Brand + logo + (mobile) hamburger */}
        <div style={styles.left}>
          <Link to="/home" style={styles.brand}>
            <img src="/logo.png" alt="ArtiBytes Logo" style={styles.logo} />
          </Link>

          {/* Hamburger (mobile only via CSS) */}
          <button
            className="nav-hamburger"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            ☰
          </button>

          {/* Desktop links */}
          <nav className="nav__links" style={styles.nav}>
            <NavLink to="/home" style={itemClass}>Home</NavLink>
            <NavLink to="/create" style={itemClass}>Create</NavLink>
            <NavLink to="/gallery" style={itemClass}>Gallery</NavLink>
            <NavLink to="/pricing" style={itemClass}>Styles</NavLink>
            <NavLink to="/creator" style={itemClass}>Royalty Program</NavLink>
            <NavLink to="/profile" style={itemClass}>Profile</NavLink>
          </nav>
        </div>

        {/* RIGHT: desktop-only actions */}
        <div className="nav__right" style={styles.right}>
          <CreditsBadge />
          {user ? (
            <>
              <span style={styles.hi}>Hi, {user.name || user.email || "@DevUser"}</span>
              <button style={styles.button} onClick={signOut}>Sign out</button>
            </>
          ) : (
            <GoogleSignInButton />
          )}
        </div>
      </div>

      {/* ===== Mobile drawer + overlay ===== */}
      {open && <div className="nav-overlay" onClick={() => setOpen(false)} />}
      <aside className={`nav-drawer ${open ? "open" : ""}`} aria-hidden={!open}>
        {/* Logo in drawer header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e5e7eb" }}>
          <img src="/logo.png" alt="ArtiBytes Logo" style={{ width: 28, height: 28 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: "#4f46e5" }}>ArtiBytes</span>
        </div>

        <nav className="drawer-nav">
          <NavLink to="/home" onClick={() => setOpen(false)}>Home</NavLink>
          <NavLink to="/create" onClick={() => setOpen(false)}>Create</NavLink>
          <NavLink to="/gallery" onClick={() => setOpen(false)}>Gallery</NavLink>
          <NavLink to="/pricing" onClick={() => setOpen(false)}>Styles</NavLink>
          <NavLink to="/creator" onClick={() => setOpen(false)}>Royalty Program</NavLink>
          <NavLink to="/profile" onClick={() => setOpen(false)}>Profile</NavLink>
        </nav>

        <div className="drawer-footer">
          <CreditsBadge />
          {user ? (
            <>
              <span>Hi, {user.name || user.email || "@DevUser"}</span>
              <button className="drawer-signout" onClick={() => { setOpen(false); signOut?.(); }}>
                Sign out
              </button>
            </>
          ) : (
            <GoogleSignInButton />
          )}
        </div>
      </aside>
    </header>
  );
}